import { postClass, postContainerClass } from "@repo/fika-blog/src/content.css"
import type { JSONContent } from "@repo/fika-blog/src/tiptap/core"
import type { IconName } from "@repo/fika-design-system/icon"
import {
  getDefaultWelcomeEmailContent,
  getDefaultWelcomeEmailSubject,
} from "@repo/fika-shared/src/i18n/welcome-email.js"
import { DEFAULT_EMOJIS } from "@repo/fika-shared/src/lib/emoji.js"
import {
  getPublicationLocales,
  type Locale,
  parseLocale,
} from "@repo/fika-shared/src/locale"
import { createQuery } from "@urql/solid"
import type { Emoji } from "solid-emoji-picker"
import { createMemo, createSignal, For, Show } from "solid-js"
import { Box } from "@/designSystem/components/box"
import { Button } from "@/designSystem/components/button"
import { EmojiPicker } from "@/designSystem/components/emoji-picker"
import { Form } from "@/designSystem/components/form"
import { Text } from "@/designSystem/components/text"
import { TextField } from "@/designSystem/components/text-field"
import {
  textFieldDescriptionClass,
  textFieldLabelClass,
} from "@/designSystem/components/text-field.css"
import { MainLayout } from "@/designSystem/layouts/main-layout"
import { ImageUpload } from "@/designSystem/patterns/image-upload"
import { Navigation } from "@/designSystem/patterns/navigation"
import { PublicationTabs } from "@/designSystem/patterns/publication-tabs"
import {
  SectionNavigator,
  type SectionNavigatorItem,
} from "@/designSystem/patterns/section-navigator"
import {
  LanguageSelector,
  TranslatableField,
  TranslatableFieldGroup,
  useTranslatableField,
} from "@/designSystem/patterns/translatable-field"
import { createFragment, graphql, type ResultOf } from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import {
  createMutationWithErrorHandling,
  ErrorFragment,
} from "@/lib/graphql-mutation-helpers"
import { toast } from "@/lib/toast"
import {
  PublicationDetailsForm,
  PublicationDetailsFragment,
} from "./_publication-details-form"
import { PublicationExportSection } from "./_publication-export-section"
import {
  PublicationLanguageForm,
  PublicationLanguageFormFragment,
} from "./_publication-language-form"
import { PublicationNameForm } from "./_publication-name-form"
import { SimpleWysiwyg } from "./editor/simple-wysiwyg"
import { mainClass } from "./post-list.css"
import {
  emojiButtonClass,
  emojiDisplayClass,
  emojiListClass,
  emojiPickerContainerClass,
  emojiRemoveClass,
  faviconPreviewClass,
  settingsClass,
} from "./settings.css"

const PublicationSettingsFragment = graphql(
  `fragment PublicationSettingsFragment on Publication {
    id
    publisherId
    name
    locale
    additionalLanguages
    updatedAt
    wordmarkUrl
    welcomeImageUrl
    faviconUrl
    blogHeader
    blogFooter
    welcomeEmailSubject
    welcomeEmailContent
    reactionEmojis
    optOutFeaturedPosts
    nameChangeCooldownDays
    ...PublicationLanguageFormFragment
    ...PublicationDetailsFragment
  }`,
  [PublicationLanguageFormFragment, PublicationDetailsFragment],
)

const UpdatePublicationMutation = graphql(
  `mutation UpdateBlogSettings($input: UpdatePublicationInput!) {
    updateCurrentPublication(input: $input) {
      ... on MutationUpdateCurrentPublicationSuccess {
        data {
          ...PublicationSettingsFragment
        }
      }
      ...ErrorFragment
    }
  }`,
  [PublicationSettingsFragment, ErrorFragment],
)

const UpdateWelcomeEmailMutation = graphql(
  `mutation UpdateWelcomeEmail($input: UpdateWelcomeEmailInput!) {
    updateWelcomeEmail(input: $input) {
      ... on MutationUpdateWelcomeEmailSuccess {
        data {
          ...PublicationSettingsFragment
        }
      }
      ...ErrorFragment
    }
  }`,
  [PublicationSettingsFragment, ErrorFragment],
)

const PublicationSettingsQuery = graphql(
  `query PublicationSettings {
    myPublication {
      ...PublicationSettingsFragment
    }
  }`,
  [PublicationSettingsFragment],
)

export function PostsSettings() {
  const { LL } = useI18nContext()
  const [result] = createQuery({ query: PublicationSettingsQuery })
  const publication = createFragment(
    PublicationSettingsFragment,
    () => result.data?.myPublication,
  )
  const publicationNameSection: SectionNavigatorItem = {
    id: "publication-name",
    title: LL().blog.blogName(),
    iconName: "user",
  }
  const publicationLanguageSection: SectionNavigatorItem = {
    id: "publication-language",
    title: LL().blog.language(),
    iconName: "languages",
  }
  const publicationDetailsSection: SectionNavigatorItem = {
    id: "publication-details",
    title: LL().blog.titleAndDescription(),
    iconName: "user-cog",
  }
  const brandingSection: SectionNavigatorItem = {
    id: "branding",
    title: LL().blog.branding(),
    iconName: "palette",
  }
  const headerSection: SectionNavigatorItem = {
    id: "header",
    title: LL().blog.header(),
    iconName: "panel-top",
  }
  const footerSection: SectionNavigatorItem = {
    id: "footer",
    title: LL().blog.footer(),
    iconName: "panel-bottom",
  }
  const welcomeEmailSection: SectionNavigatorItem = {
    id: "welcome-email",
    title: LL().blog.welcomeEmail.title(),
    iconName: "mail",
  }
  const reactionEmojisSection: SectionNavigatorItem = {
    id: "reaction-emojis",
    title: LL().blog.reactionEmojis(),
    iconName: "smile",
  }
  const publicationExportSection: SectionNavigatorItem = {
    id: "publication-export",
    title: LL().blog.export.title(),
    iconName: "download-cloud",
  }
  const sections = [
    publicationNameSection,
    publicationLanguageSection,
    publicationDetailsSection,
    brandingSection,
    headerSection,
    footerSection,
    welcomeEmailSection,
    reactionEmojisSection,
    publicationExportSection,
  ]

  return (
    <MainLayout
      hue="amber"
      header={<Navigation hue="amber" title={LL().blog.title()} />}
    >
      <div class={mainClass}>
        <PublicationTabs />
        <SectionNavigator sections={sections}>
          <div class={settingsClass}>
            <PublicationNameForm
              title={publicationNameSection.title}
              id={publicationNameSection.id}
              iconName={publicationNameSection.iconName}
              nameChangeCooldownDays={() =>
                publication()?.nameChangeCooldownDays
              }
            />
            <Show when={publication()} keyed>
              {publication => (
                <>
                  <PublicationLanguageForm
                    id={publicationLanguageSection.id}
                    title={publicationLanguageSection.title}
                    iconName={publicationLanguageSection.iconName}
                    publication={publication}
                  />
                  <PublicationDetailsForm
                    publication={publication}
                    title={publicationDetailsSection.title}
                    id={publicationDetailsSection.id}
                    iconName={publicationDetailsSection.iconName}
                  />
                  <BrandingForm
                    title={brandingSection.title}
                    iconName={brandingSection.iconName}
                    id={brandingSection.id}
                    publication={publication}
                  />
                  <Show when={publication.name}>
                    <SectionEditor
                      publication={publication}
                      attributeName="blogHeader"
                      title={headerSection.title}
                      description={LL().blog.headerDescription()}
                      iconName={headerSection.iconName}
                      id={headerSection.id}
                      translatable
                    />
                    <SectionEditor
                      publication={publication}
                      attributeName="blogFooter"
                      title={footerSection.title}
                      description={LL().blog.footerDescription()}
                      iconName={footerSection.iconName}
                      id={footerSection.id}
                      translatable
                    />
                    <WelcomeEmailForm
                      title={welcomeEmailSection.title}
                      iconName={welcomeEmailSection.iconName}
                      id={welcomeEmailSection.id}
                      publication={publication}
                    />
                    <ReactionEmojisForm
                      title={reactionEmojisSection.title}
                      iconName={reactionEmojisSection.iconName}
                      id={reactionEmojisSection.id}
                      publication={publication}
                    />
                    <PublicationExportSection
                      title={publicationExportSection.title}
                      iconName={publicationExportSection.iconName}
                      id={publicationExportSection.id}
                    />
                  </Show>
                </>
              )}
            </Show>
          </div>
        </SectionNavigator>
      </div>
    </MainLayout>
  )
}

type PublicationSettings = ResultOf<typeof PublicationSettingsFragment>

interface SectionEditorProps {
  publication: PublicationSettings
  attributeName: "blogHeader" | "blogFooter"
  title: string
  description: string
  iconName: IconName
  id: string
  translatable?: boolean
}

function SectionEditor(props: SectionEditorProps) {
  const { LL } = useI18nContext()
  const [updatePublicationState, updatePublication] =
    createMutationWithErrorHandling(
      UpdatePublicationMutation,
      "updateCurrentPublication",
    )

  const publicationLocale = createMemo(() =>
    parseLocale(props.publication.locale),
  )
  const availableLocales = createMemo(() =>
    getPublicationLocales(
      publicationLocale(),
      props.publication.additionalLanguages,
    ),
  )
  const [draftContent, setDraftContent] = createSignal(
    props.publication[props.attributeName] ?? "",
  )

  async function handleSave(value: string) {
    const state = await updatePublication({
      input: { [props.attributeName]: value },
    })
    if (state.success) {
      setDraftContent(value)
      toast.success(LL().settings.informationSaved())
    }
  }

  return (
    <Form
      title={props.title}
      description={props.description}
      hue="amber"
      iconName={props.iconName}
      id={props.id}
    >
      <div class={postContainerClass}>
        <div class={postClass}>
          <Show
            when={props.translatable}
            fallback={
              <SimpleWysiwyg
                publicationName={props.publication.name}
                content={draftContent}
                title={props.title}
                onSave={async json => {
                  await handleSave(JSON.stringify(json))
                }}
                isSaving={updatePublicationState.fetching}
                blocks={{
                  image: {
                    uploadFolder: ["publication", props.publication.id],
                  },
                  horizontalRule: true,
                  codeBlock: true,
                  quote: true,
                  subscribe: true,
                }}
              />
            }
          >
            <TranslatableFieldGroup
              availableLocales={availableLocales()}
              defaultLocale={publicationLocale()}
            >
              <TranslatableField
                value={draftContent()}
                onChange={setDraftContent}
                availableLocales={availableLocales()}
                defaultLocale={publicationLocale()}
                name={props.attributeName}
                format="tiptap"
              >
                {(value, onChange) => (
                  <SimpleWysiwyg
                    publicationName={props.publication.name}
                    content={value}
                    title={props.title}
                    onSave={async json => {
                      await onChange(JSON.stringify(json))
                      await handleSave(draftContent())
                    }}
                    isSaving={updatePublicationState.fetching}
                    blocks={{
                      image: {
                        uploadFolder: ["publication", props.publication.id],
                      },
                      horizontalRule: true,
                      codeBlock: true,
                      quote: true,
                      subscribe: true,
                    }}
                    dialogContent={<LanguageSelector />}
                  />
                )}
              </TranslatableField>
            </TranslatableFieldGroup>
          </Show>
        </div>
      </div>
    </Form>
  )
}

function WelcomeEmailForm(props: {
  publication: PublicationSettings
  title: string
  iconName: IconName
  id: string
}) {
  const { LL } = useI18nContext()
  const [updateState, updateWelcomeEmail] = createMutationWithErrorHandling(
    UpdateWelcomeEmailMutation,
    "updateWelcomeEmail",
  )

  const publicationLocale = createMemo(() =>
    parseLocale(props.publication.locale),
  )
  const availableLocales = createMemo(() =>
    getPublicationLocales(
      publicationLocale(),
      props.publication.additionalLanguages,
    ),
  )

  const [latestSubject, setLatestSubject] = createSignal(
    props.publication.welcomeEmailSubject ?? "",
  )
  const [latestContent, setLatestContent] = createSignal(
    props.publication.welcomeEmailContent ?? "",
  )

  function getDefaultSubject(locale: Locale) {
    return getDefaultWelcomeEmailSubject(locale, props.publication.name ?? "")
  }

  function getDefaultContent(locale: Locale) {
    return getDefaultWelcomeEmailContent(locale, props.publication.name ?? "")
  }

  async function saveWelcomeEmail() {
    const subject = latestSubject().trim()
    const content = latestContent().trim()
    const state = await updateWelcomeEmail({
      input: {
        subject: subject || null,
        content: content || null,
      },
    })

    if (state.success) {
      toast.success(LL().settings.informationSaved())
    }
  }

  return (
    <Form
      title={props.title}
      description={LL().blog.welcomeEmail.description()}
      hue="amber"
      iconName={props.iconName}
      id={props.id}
    >
      <div class={postContainerClass}>
        <div class={postClass}>
          <TranslatableFieldGroup
            availableLocales={availableLocales()}
            defaultLocale={publicationLocale()}
          >
            <TranslatableField
              value={props.publication.welcomeEmailSubject ?? ""}
              onChange={setLatestSubject}
              availableLocales={availableLocales()}
              defaultLocale={publicationLocale()}
              name="subject"
              format="text"
            >
              {(subjectValue, onSubjectChange) => {
                const translatableContext = useTranslatableField()
                const selectedLocale = createMemo(
                  () =>
                    translatableContext?.currentLocale() ?? publicationLocale(),
                )

                return (
                  <>
                    <Text fontSize="l" color="sand900" fontWeight="500">
                      {subjectValue() || getDefaultSubject(selectedLocale())}
                    </Text>
                    <TranslatableField
                      value={props.publication.welcomeEmailContent ?? ""}
                      onChange={setLatestContent}
                      availableLocales={availableLocales()}
                      defaultLocale={publicationLocale()}
                      name="content"
                      format="tiptap"
                    >
                      {(contentValue, onContentChange) => (
                        <SimpleWysiwyg
                          label={LL().blog.welcomeEmail.subjectContent()}
                          content={() =>
                            contentValue() ||
                            JSON.stringify(getDefaultContent(selectedLocale()))
                          }
                          title={LL().blog.welcomeEmail.title()}
                          onSave={async (json: JSONContent) => {
                            await onSubjectChange(subjectValue())
                            await onContentChange(JSON.stringify(json))
                            await saveWelcomeEmail()
                          }}
                          isSaving={updateState.fetching}
                          publicationName={props.publication.name}
                          blocks={{
                            image: {
                              uploadFolder: [
                                "publication",
                                props.publication.id,
                              ],
                            },
                            horizontalRule: true,
                            codeBlock: true,
                            quote: true,
                          }}
                          dialogContent={
                            <>
                              <LanguageSelector />
                              <TextField
                                value={subjectValue()}
                                onChange={onSubjectChange}
                              >
                                <TextField.Label>
                                  {LL().blog.welcomeEmail.subjectLabel()}
                                </TextField.Label>
                                <TextField.Input
                                  placeholder={getDefaultSubject(
                                    selectedLocale(),
                                  )}
                                />
                              </TextField>
                            </>
                          }
                        />
                      )}
                    </TranslatableField>
                  </>
                )
              }}
            </TranslatableField>
          </TranslatableFieldGroup>
        </div>
      </div>
    </Form>
  )
}

function BrandingForm(props: {
  publication: PublicationSettings
  title: string
  iconName: IconName
  id: string
}) {
  const { LL } = useI18nContext()
  const [updatePublicationState, updatePublication] =
    createMutationWithErrorHandling(
      UpdatePublicationMutation,
      "updateCurrentPublication",
    )

  async function onSubmitBranding() {
    const state = await updatePublication({
      input: {
        wordmarkUrl: formWordmark(),
        welcomeImageUrl: formWelcomeImage(),
        faviconUrl: formFavicon(),
      },
    })
    if (state.success) {
      toast.success(LL().settings.informationSaved())
    }
  }
  const defaultBranding = createMemo(() => {
    return {
      wordmark: props.publication.wordmarkUrl,
      welcomeImage: props.publication.welcomeImageUrl,
      favicon: props.publication.faviconUrl,
    }
  })

  const [formWordmark, setFormWordmark] = createSignal(
    defaultBranding().wordmark,
  )
  const [formWelcomeImage, setFormWelcomeImage] = createSignal(
    defaultBranding().welcomeImage,
  )
  const [formFavicon, setFormFavicon] = createSignal(defaultBranding().favicon)

  const brandingDirty = createMemo(
    () =>
      formWordmark() !== defaultBranding().wordmark ||
      formWelcomeImage() !== defaultBranding().welcomeImage ||
      formFavicon() !== defaultBranding().favicon,
  )

  return (
    <Form
      title={props.title}
      iconName={props.iconName}
      id={props.id}
      description={LL().blog.brandingDescription()}
      hue="amber"
      onSubmit={onSubmitBranding}
    >
      <TextField>
        <TextField.Label>{LL().blog.wordmark()}</TextField.Label>
        <ImageUpload
          text={LL().blog.uploadWordmark()}
          imageSrc={formWordmark()}
          onUpload={setFormWordmark}
          hue="amber"
          uploadFolder={["publication", props.publication.id]}
        />
        <TextField.Description>
          {LL().blog.wordmarkDescription()}
        </TextField.Description>
      </TextField>
      <TextField>
        <TextField.Label>{LL().blog.subscribeImage()}</TextField.Label>
        <ImageUpload
          text={LL().blog.uploadSubscribeImage()}
          imageSrc={formWelcomeImage()}
          onUpload={setFormWelcomeImage}
          hue="amber"
          uploadFolder={["publication", props.publication.id]}
        />
        <TextField.Description>
          {LL().blog.subscribeImageDescription()}
        </TextField.Description>
      </TextField>
      <TextField>
        <TextField.Label>{LL().blog.favicon()}</TextField.Label>
        <ImageUpload
          text={LL().blog.uploadFavicon()}
          imageSrc={formFavicon()}
          onUpload={setFormFavicon}
          hue="amber"
          uploadFolder={["publication", props.publication.id]}
          imgClass={faviconPreviewClass}
        />
        <TextField.Description>
          {LL().blog.faviconDescription()}
        </TextField.Description>
      </TextField>
      <Form.Save
        hue="amber"
        disabled={!brandingDirty()}
        active={updatePublicationState.fetching}
      />
    </Form>
  )
}

function ReactionEmojisForm(props: {
  publication: PublicationSettings
  title: string
  iconName: IconName
  id: string
}) {
  const { LL } = useI18nContext()
  const UpdateReactionEmojisMutation = graphql(
    `mutation UpdateReactionEmojis($reactionEmojis: String!) {
      updateReactionEmojis(reactionEmojis: $reactionEmojis) {
        ... on MutationUpdateReactionEmojisSuccess {
          data {
            ...PublicationSettingsFragment
          }
        }
        ...ErrorFragment
      }
    }`,
    [PublicationSettingsFragment, ErrorFragment],
  )

  const [updateState, updateReactionEmojis] = createMutationWithErrorHandling(
    UpdateReactionEmojisMutation,
    "updateReactionEmojis",
  )

  const [formEmojis, setFormEmojis] = createSignal(
    props.publication.reactionEmojis ?? DEFAULT_EMOJIS,
  )
  const [showPicker, setShowPicker] = createSignal(false)

  const selectedEmojisList = createMemo(() =>
    formEmojis()
      .split(",")
      .map((emoji: string) => emoji.trim())
      .filter(Boolean),
  )

  const dirty = createMemo(
    () => formEmojis() !== (props.publication.reactionEmojis ?? DEFAULT_EMOJIS),
  )

  function removeEmoji(emoji: string) {
    const emojis = selectedEmojisList().filter((e: string) => e !== emoji)
    setFormEmojis(emojis.join(","))
  }

  function onEmojiClick(emoji: Emoji) {
    const emojiChar = emoji.emoji
    const currentEmojis = selectedEmojisList()

    if (currentEmojis.includes(emojiChar)) {
      return
    }

    setFormEmojis([...currentEmojis, emojiChar].join(","))
  }

  async function onSubmit() {
    const state = await updateReactionEmojis({ reactionEmojis: formEmojis() })
    if (state.success) {
      toast.success(LL().settings.informationSaved())
    }
  }

  return (
    <Form
      title={props.title}
      description={LL().blog.reactionEmojisDescription()}
      hue="amber"
      iconName={props.iconName}
      id={props.id}
      onSubmit={onSubmit}
    >
      <Box gap="4">
        <span class={textFieldLabelClass}>{LL().blog.reactionEmojis()}</span>

        <Box gap="8" flexDirection="row" alignItems="flex-start">
          <div class={emojiListClass}>
            <For each={selectedEmojisList()}>
              {emoji => (
                <button
                  type="button"
                  class={emojiButtonClass}
                  onClick={() => removeEmoji(emoji)}
                  title={`Remove ${emoji}`}
                >
                  <span class={emojiDisplayClass}>{emoji}</span>
                  <span class={emojiRemoveClass}>×</span>
                </button>
              )}
            </For>
          </div>

          <Button
            type="button"
            hue="amber"
            kind="soft"
            onClick={() => setShowPicker(!showPicker())}
          >
            {LL().blog.addEmoji()}
          </Button>
        </Box>

        <Show when={showPicker()}>
          <div class={emojiPickerContainerClass}>
            <EmojiPicker onEmojiClick={onEmojiClick} />
          </div>
        </Show>

        <span class={textFieldDescriptionClass}>
          {LL().blog.reactionEmojisDescription()}
        </span>
      </Box>

      <Form.Save
        hue="amber"
        disabled={!dirty()}
        active={updateState.fetching}
      />
    </Form>
  )
}
